/**
 * Authentication Context
 * Manages user authentication, roles, and privilege verification
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { ROLE_PRIVILEGES, expandEffectivePrivileges } from '../config/roles';
import apiClient, { setSessionInvalidHandler } from '../api/client';

const AuthContext = createContext();

const effectivePrivileges = (u) => {
  if (!u) return [];
  const base = Array.isArray(u.custom_privileges)
    ? u.custom_privileges
    : ROLE_PRIVILEGES[String(u.role || '').toLowerCase()] || [];
  return expandEffectivePrivileges(base);
};

const samePrivilegeSet = (a, b) => {
  const sa = [...new Set(a)].sort();
  const sb = [...new Set(b)].sort();
  return sa.length === sb.length && sa.every((v, i) => v === sb[i]);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessNotice, setAccessNotice] = useState(null);

  const logout = useCallback(() => {
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('googleToken');
  }, []);

  /**
   * Pull the latest user record (role + custom_privileges) from the server and
   * merge it over the cached session. This lets privilege changes made by an
   * admin take effect for an already-logged-in user without a manual reload.
   */
  const refreshUser = useCallback(async () => {
    try {
      const storedUser = localStorage.getItem('user');
      if (!storedUser) return;
      const parsed = JSON.parse(storedUser);
      if (!parsed?.token) return;

      const res = await apiClient.get('/api-v1/auth/me');
      if (res.data?.success && res.data?.data) {
        const data = res.data.data;
        const refreshed = {
          ...parsed,
          ...data,
          role: String(data.role || parsed.role || 'rep').toLowerCase(),
          // custom_privileges may legitimately be null (reset to role defaults);
          // always take the server value so removals propagate too.
          custom_privileges:
            data.custom_privileges !== undefined
              ? data.custom_privileges
              : parsed.custom_privileges,
          token: parsed.token,
        };

        // Notify the user if an admin changed their access while they were
        // already signed in (role change or privilege grant/revoke).
        const roleChanged = String(parsed.role || '').toLowerCase() !== refreshed.role;
        const privsChanged = !samePrivilegeSet(
          effectivePrivileges(parsed),
          effectivePrivileges(refreshed)
        );
        if (roleChanged || privsChanged) {
          setAccessNotice(
            roleChanged
              ? `Your role was changed to "${refreshed.role}". Your access has been updated.`
              : 'Your access permissions were updated by an administrator.'
          );
        }

        setUser(refreshed);
        localStorage.setItem('user', JSON.stringify(refreshed));
      }
    } catch (error) {
      const status = error.response?.status;
      if (status === 401 || status === 403) {
        logout();
      }
    }
  }, [logout]);

  // Initialize user from localStorage and validate JWT with the server
  useEffect(() => {
    setSessionInvalidHandler(logout);

    const initAuth = async () => {
      try {
        const storedUser = localStorage.getItem('user');
        if (!storedUser) return;

        const parsed = JSON.parse(storedUser);
        if (!parsed?.token) {
          logout();
          return;
        }

        setUser(parsed);
        await refreshUser();
      } catch (error) {
        console.error('Failed to initialize auth:', error);
        logout();
      } finally {
        setLoading(false);
      }
    };

    initAuth();
    return () => setSessionInvalidHandler(null);
  }, [logout, refreshUser]);

  // Re-sync privileges whenever the tab regains focus / becomes visible, and on
  // a periodic interval, so admin-made privilege changes apply without a reload.
  useEffect(() => {
    const onFocus = () => {
      if (localStorage.getItem('user')) refreshUser();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') onFocus();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    const interval = setInterval(onFocus, 60000);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      clearInterval(interval);
    };
  }, [refreshUser]);

  /**
   * Check if user has a specific privilege
   * Masters both role-based and custom privileges
   */
  const hasPrivilege = (privilege) => {
    if (!user) return false;
    return effectivePrivileges(user).includes(privilege);
  };

  /**
   * Check if user has any of the given privileges
   */
  const hasAnyPrivilege = (privileges) => {
    if (!user) return false;
    return privileges.some(p => hasPrivilege(p));
  };

  /**
   * Check if user has all of the given privileges
   */
  const hasAllPrivileges = (privileges) => {
    if (!user) return false;
    return privileges.every(p => hasPrivilege(p));
  };

  /**
   * Get all privileges for the current user
   * Includes both custom and role-based privileges
   */
  const getUserPrivileges = () => {
    if (!user) return [];
    return effectivePrivileges(user);
  };

  /**
   * Login user
   */
  const login = (userData) => {
    const normalized = {
      ...userData,
      role: String(userData.role || 'rep').toLowerCase(),
      name:
        userData.name?.trim() ||
        userData.email?.split('@')[0] ||
        'User',
    };
    setUser(normalized);
    localStorage.setItem('user', JSON.stringify(normalized));
  };

  /**
   * Update user role
   */
  const updateUserRole = (newRole) => {
    if (user) {
      const updatedUser = { ...user, role: newRole };
      setUser(updatedUser);
    }
  };

  // Auto-dismiss the access-change toast.
  useEffect(() => {
    if (!accessNotice) return undefined;
    const t = setTimeout(() => setAccessNotice(null), 8000);
    return () => clearTimeout(t);
  }, [accessNotice]);

  const value = {
    user,
    loading,
    hasPrivilege,
    hasAnyPrivilege,
    hasAllPrivileges,
    getUserPrivileges,
    login,
    logout,
    updateUserRole,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {accessNotice && (
        <div className="fixed bottom-4 right-4 z-[9999] max-w-sm">
          <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-white shadow-lg px-4 py-3">
            <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-600 text-sm font-bold">
              i
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-900">Access updated</p>
              <p className="text-sm text-gray-600">{accessNotice}</p>
            </div>
            <button
              onClick={() => setAccessNotice(null)}
              className="text-gray-400 hover:text-gray-600 text-lg leading-none"
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
};

/**
 * Hook to use auth context
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export default AuthContext;
